import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_ENDPOINTS } from "../config/api";
import {
  FaUsers,
  FaDonate,
  FaVideo,
  FaPray,
  FaImages,
  FaPlus,
  FaLink,
  FaCalendarAlt,
  FaOm,
  FaClock,
  FaCheckCircle
} from "react-icons/fa";
import "../styles/Dashboard.css";

const Dashboard = () => {
  /* ================= STATE ================= */
  const [stats, setStats] = useState({
    usersCount: 0,
    donationsSum: 0,
    donationsCount: 0,
    successfulCount: 0,
    poojasCount: 0,
    galleryCount: 0,
    festivalsCount: 0,
    idolsCount: 0,
    timingsCount: 0,
  });

  const [recentUsers, setRecentUsers] = useState([]);
  const [recentDonations, setRecentDonations] = useState([]);
  const [recentPoojas, setRecentPoojas] = useState([]);

  // Dynamic greetings
  const [greeting, setGreeting] = useState("Welcome");

  useEffect(() => {
    fetchDashboardData();
    calculateGreeting();
  }, []);

  const calculateGreeting = () => {
    const hr = new Date().getHours();
    if (hr < 12) setGreeting("Good Morning");
    else if (hr < 17) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");
  };

  /* ================= FETCH DASHBOARD DATA ================= */
  const fetchDashboardData = async () => {
    try {
      
      // Fetch users
      const usersRes = await fetch(API_ENDPOINTS.USER_ALL);
      const usersData = await usersRes.json();
      const users = Array.isArray(usersData) ? usersData : [];
      
      // Fetch donations
      const donRes = await fetch(API_ENDPOINTS.DONATIONS);
      const donData = await donRes.json();
      const donations = Array.isArray(donData) ? donData : [];
      
      // Fetch poojas
      const poojaRes = await fetch(API_ENDPOINTS.POOJAS);
      const poojaData = await poojaRes.json();
      const poojas = Array.isArray(poojaData) ? poojaData : [];

      // Fetch gallery
      const galleryRes = await fetch(API_ENDPOINTS.GALLERY);
      const galleryData = await galleryRes.json();

      // Fetch festivals
      const festRes = await fetch(API_ENDPOINTS.FESTIVALS);
      const festData = await festRes.json();
      const festivals = festData?.festivals || [];

      // Fetch idols
      const idolRes = await fetch(API_ENDPOINTS.IDOL);
      const idolData = await idolRes.json();
      const idols = Array.isArray(idolData) ? idolData : [];

      // Fetch timings
      const timingRes = await fetch(API_ENDPOINTS.TIMINGS);
      const timingData = await timingRes.json();
      const timingsCount = timingData?.schedule?.length || 0;

      // Calculations
      const approvedDonations = donations.filter(item => item.status?.toLowerCase() === "approved" || item.status?.toLowerCase() === "success");
      const totalDonations = approvedDonations.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
      const successfulPayments = approvedDonations.length;

      setStats({
        usersCount: users.length,
        donationsSum: totalDonations,
        donationsCount: donations.length,
        successfulCount: successfulPayments,
        poojasCount: poojas.length,
        galleryCount: (galleryData?.photos?.length || 0) + (galleryData?.videos?.length || 0),
        festivalsCount: festivals.length,
        idolsCount: idols.length,
        timingsCount: timingsCount,
      });

      setRecentUsers(users.slice(-5).reverse());
      setRecentDonations(donations.slice(-5).reverse());
      setRecentPoojas(poojas.slice(0, 5));
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    }
  };

  const getLocalDate = () => {
    return new Date().toLocaleDateString("en-IN", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  return (
    <div className="admin-dashboard-container">
      {/* HEADER BANNER */}
      <div className="dashboard-welcome-banner">
        <div className="welcome-text">
          <h1>🙏 Om Sai Ram, {greeting}!</h1>
          <p>Sri Gnana Sai Baba Mandir CMS Portal — system health and daily activity dashboard.</p>
          <span className="current-date-badge">{getLocalDate()}</span>
        </div>
      </div>

      {/* DETAILED STATS GRID */}
      <div className="detailed-stats-grid">
        <div className="stat-metric-card users">
          <div className="metric-icon"><FaUsers /></div>
          <div className="metric-details">
            <h3>{stats.usersCount}</h3>
            <p>Registered Users</p>
          </div>
        </div>

        <div className="stat-metric-card donations">
          <div className="metric-icon"><FaDonate /></div>
          <div className="metric-details">
            <h3>₹{stats.donationsSum.toLocaleString("en-IN")}</h3>
            <p>Total Funds Raised</p>
          </div>
        </div>

        <div className="stat-metric-card payments">
          <div className="metric-icon"><FaCheckCircle /></div>
          <div className="metric-details">
            <h3>{stats.successfulCount} <span className="sub-text">/ {stats.donationsCount}</span></h3>
            <p>Successful Payments</p>
          </div>
        </div>

        <div className="stat-metric-card poojas">
          <div className="metric-icon"><FaPray /></div>
          <div className="metric-details">
            <h3>{stats.poojasCount}</h3>
            <p>Poojas Managed</p>
          </div>
        </div>

        <div className="stat-metric-card festivals">
          <div className="metric-icon"><FaCalendarAlt /></div>
          <div className="metric-details">
            <h3>{stats.festivalsCount}</h3>
            <p>Festivals Listed</p>
          </div>
        </div>

        <div className="stat-metric-card idols">
          <div className="metric-icon"><FaOm /></div>
          <div className="metric-details">
            <h3>{stats.idolsCount}</h3>
            <p>Temple Idols</p>
          </div>
        </div>

        <div className="stat-metric-card gallery">
          <div className="metric-icon"><FaImages /></div>
          <div className="metric-details">
            <h3>{stats.galleryCount}</h3>
            <p>Gallery Items</p>
          </div>
        </div>

        <div className="stat-metric-card timings">
          <div className="metric-icon"><FaClock /></div>
          <div className="metric-details">
            <h3>{stats.timingsCount}</h3>
            <p>Schedules Listed</p>
          </div>
        </div>
      </div>

      {/* DASHBOARD COLUMNS WORKSPACE */}
      <div className="dashboard-grid-workspace">
        
        {/* ROW 1: QUICK ACTIONS & RECENT DONATIONS */}
        <div className="workspace-column-box">
          <div className="box-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="quick-shortcuts-grid">
            <Link to="/admin/live-darshan" className="shortcut-item">
              <FaVideo className="icon" />
              <span>Set Live Stream</span>
            </Link>
            <Link to="/admin/poojas" className="shortcut-item">
              <FaPlus className="icon" />
              <span>Add Pooja Details</span>
            </Link>
            <Link to="/admin/gallery" className="shortcut-item">
              <FaImages className="icon" />
              <span>Upload Gallery Image</span>
            </Link>
            <Link to="/admin/links" className="shortcut-item">
              <FaLink className="icon" />
              <span>Update Spiritual Links</span>
            </Link>
          </div>
        </div>

        {/* ROW 2: RECENT DONATIONS */}
        <div className="workspace-column-box">
          <div className="box-header">
            <h3>Recent Donations</h3>
          </div>
          <div className="activity-list-container">
            {recentDonations.length > 0 ? (
              recentDonations.map((don, idx) => (
                <div className="activity-item-row" key={don._id || idx}>
                  <div className="activity-main-info">
                    <h4>{don.donarName || don.donorName || don.name || "Anonymous Donor"}</h4>
                    <p>{don.email || "No email address listed"}</p>
                  </div>
                  <div className="activity-side-meta">
                    <span className="funds-green-tag">₹{Number(don.amount || 0).toLocaleString("en-IN")}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-activity-state">No donation logs found in database.</div>
            )}
          </div>
        </div>

        {/* ROW 3: NEW USER REGISTRATIONS */}
        <div className="workspace-column-box">
          <div className="box-header">
            <h3>New Users</h3>
          </div>
          <div className="activity-list-container">
            {recentUsers.length > 0 ? (
              recentUsers.map((user) => (
                <div className="activity-item-row" key={user._id}>
                  <img
                    src={user.profileImage || "/images/default-user.png"}
                    alt={user.fullName}
                    className="user-profile-avatar"
                    onError={(e) => {
                      e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
                    }}
                  />
                  <div className="activity-main-info">
                    <h4>{user.fullName || "N/A"}</h4>
                    <p>{user.email || "No email listed"}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-activity-state">No registered user logs found.</div>
            )}
          </div>
        </div>

        {/* ROW 4: ACTIVE TEMPLE POOJAS */}
        <div className="workspace-column-box">
          <div className="box-header">
            <h3>Active Pooja Lists</h3>
          </div>
          <div className="activity-list-container">
            {recentPoojas.length > 0 ? (
              recentPoojas.map((pooja, idx) => (
                <div className="activity-item-row" key={pooja._id || idx}>
                  <div className="pooja-avatar-badge">{pooja.icon || "🪔"}</div>
                  <div className="activity-main-info">
                    <h4>{pooja.name}</h4>
                    <p>{pooja.time || "Daily Ritual"}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-activity-state">No poojas configured in CMS.</div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
